<script>
import {onMounted, ref} from 'vue';
import {useMenuStore} from '@/js/store/MenuStore.js';
import {useCartStore} from '@/js/store/CartStore.js';
import {useLikeStore} from '@/js/store/LikeStore.js';
import IconLogo from '@js/components/Icons/IconLogo.vue';
import IconMapPoint from '@js/components/Icons/IconMapPoint.vue';
import IconAngleLeft from '@js/components/Icons/IconAngleLeft.vue';
import IconX from '@js/components/Icons/IconX.vue';
import SalesDepartmentPhone from '@js/components/Sections/Links/SalesDepartmentPhone.vue';
import {Link} from '@inertiajs/vue3';
import {useCompareStore} from "@js/store/CompareStore.js";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {usePopupsControlStore} from "@js/store/PopupsControlStore.js";
import IconUser from "@js/components/Icons/IconUser.vue";
import MobileMenuAccordionBlock from "@js/components/Mobile/MobileMenuAccordionBlock.vue";

export default {
    name: "MobileProfileMenu",
    components: {
        MobileMenuAccordionBlock,
        IconUser,
        OnlyMobile,
        IconLogo,
        IconMapPoint,
        IconAngleLeft,
        IconX,
        SalesDepartmentPhone,
        Link,
    },
    setup() {
        const menuStore = useMenuStore();
        const cartStore = useCartStore();
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();
        const popupStore = usePopupsControlStore();

        const touchStartX = ref(0);
        const touchDeltaX = ref(0);

        const onTouchStart = (e) => {
            touchStartX.value = e.touches[0].clientX;
        };

        const onTouchMove = (e) => {
            touchDeltaX.value = e.touches[0].clientX - touchStartX.value;
        };

        const onTouchEnd = () => {
            if (touchDeltaX.value < -50) {
                menuStore.toggleProfileSidenav();
            }
            touchDeltaX.value = 0;
        };

        const openCatalogMenu = () => {
            menuStore.toggleProfileSidenav();
            menuStore.toggleSidenav();
        };

        const openCitySelectPopup = () => {
            menuStore.toggleProfileSidenav();
            popupStore.showCitySelectPopup();
        };

        const isClient = ref(false);

        onMounted(() => {
            isClient.value = true;
        });

        const demolitionItems = [
            {
                title: 'Демонтаж металлоконструкций',
                route_name: 'demontazhMetallokonstrukciy',
            },
            {
                title: 'Демонтаж ангаров',
                route_name: 'demontazhAngarov',
            },
            {
                title: 'Демонтаж гаражей',
                route_name: 'demontazhGarazhey',
            },
            {
                title: 'Демонтаж кранов',
                route_name: 'demontazhKranov',
            },
            {
                title: 'Демонтаж котельных',
                route_name: 'demontazhKotelnyh',
            },
            {
                title: 'Демонтаж торговых павильонов',
                route_name: 'demontazhTorgovyhPavilonov',
            },
            {
                title: 'Демонтаж складов',
                route_name: 'demontazhSkladov',
            },
            {
                title: 'Демонтаж заводов',
                route_name: 'demontazhZavodov',
            },
            {
                title: 'Демонтаж цеха',
                route_name: 'demontazhCehov',
            },
            {
                title: 'Утилизация спецтехники',
                route_name: 'demontazhSpectehniki',
            },
        ];

        const mainLinks = [
            {
                title: 'Вывоз',
                route_name: 'deliveryAndShipping',
            },
            {
                title: 'Как сдать',
                route_name: 'howToHandOver',
            },
            {
                title: 'Прайс-лист',
                route_name: 'priceList',
            },
        ];

        const userLinks = [
            {
                title: 'Избранные товары',
                route_name: 'likedProducts',
                badgeKey: 'liked',
            },
            {
                title: 'Мой расчёт',
                route_name: 'cart',
                badgeKey: 'cart',
            },
        ];

        const infoLinks = [
            {
                title: 'Обратная связь',
                route_name: 'contacts',
            },
            {
                title: 'О компании',
                route_name: 'aboutCompany',
            },
            {
                title: 'Политика конфиденциальности',
                route_name: 'privacyPolicy',
            },
        ];

        const getBadgeValue = (badgeKey) => {
            if (!isClient.value) return 0;
            if (badgeKey === 'liked') return likeStore.likedIds.length;
            if (badgeKey === 'cart') return cartStore.items.length;
            if (badgeKey === 'compare') return compareStore.comparedIds.length;
            return 0;
        };

        return {
            isClient,
            menuStore,
            cartStore,
            likeStore,
            compareStore,
            popupStore,
            openCatalogMenu,
            onTouchStart,
            onTouchMove,
            onTouchEnd,
            openCitySelectPopup,
            demolitionItems,
            mainLinks,
            userLinks,
            infoLinks,
            getBadgeValue,
        };
    },
};
</script>

<template>
    <div>
        <div
            class="fixed inset-y-0 left-0 z-50 w-full max-w-[480px] bg-white shadow-xl transform transition-transform duration-300"
            :class="{
                '-translate-x-full': !menuStore.isProfileSidenavOpen,
                'translate-x-0': menuStore.isProfileSidenavOpen
            }"
            @touchstart.passive="onTouchStart"
            @touchmove.passive="onTouchMove"
            @touchend="onTouchEnd"
        >
            <div class="flex flex-col h-full">
                <div class="flex items-center justify-between px-4 pt-4 pb-4 border-b border-gray-200">
                    <IconLogo class="h-auto w-[190px]"/>

                    <button
                        @click.prevent="menuStore.toggleProfileSidenav"
                        title="Закрыть"
                        aria-label="Закрыть"
                        class="rounded-md focus:outline-none"
                    >
                        <IconX class="w-6 h-6"/>
                    </button>
                </div>

                <div class="flex-1 overflow-y-auto px-4 py-5">
                        <div class="pb-5 border-b border-gray-200">
                            <button
                                @click.prevent="openCatalogMenu"
                                class="w-full flex items-center justify-between font-medium text-left"
                            >
                                <span>Меню каталога</span>
                                <IconAngleLeft class="w-5 h-5 rotate-180"/>
                            </button>
                        </div>

                    <div class="flex flex-col gap-5 mt-5 pb-5 border-b border-gray-200">
                        <div
                            v-if="popupStore.selectedCity.name"
                            @click.prevent="openCitySelectPopup"
                            class="flex items-center justify-between font-medium cursor-pointer"
                        >
                            <div>
                                Город
                            </div>

                            <div class="flex items-center gap-2 text-sm">
                                <IconMapPoint class="w-5 h-5 text-navHoverColor"/>
                                <span>{{ popupStore.selectedCity.name }}</span>
                                <IconAngleLeft class="w-5 h-5 rotate-180"/>
                            </div>
                        </div>

                        <Link
                            v-for="(item, index) in mainLinks"
                            :key="`main-${index}`"
                            :href="route(item.route_name)"
                            class="block font-medium"
                        >
                            <span>{{ item.title }}</span>
                        </Link>
                    </div>

                    <div class="flex flex-col gap-5 mt-5 pb-5 border-b border-gray-200">
                        <Link
                            v-for="(item, index) in userLinks"
                            :key="`user-${index}`"
                            :href="route(item.route_name)"
                            class="flex items-center justify-between font-medium"
                        >
                            <span>{{ item.title }}</span>

                            <div
                                v-if="getBadgeValue(item.badgeKey)"
                                class="flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-red-600 rounded-full text-xs font-semibold text-white"
                            >
                                {{ getBadgeValue(item.badgeKey) }}
                            </div>
                        </Link>
                    </div>

                    <div class="mt-5">
                        <MobileMenuAccordionBlock
                            title="Демонтаж"
                            :items="demolitionItems"
                        />
                    </div>

                    <div class="flex flex-col gap-5 mt-5 pb-5">
                        <div class="font-medium text-gray-500">
                            Информация
                        </div>

                        <Link
                            v-for="(item, index) in infoLinks"
                            :key="`info-${index}`"
                            :href="route(item.route_name)"
                            class="block font-medium"
                        >
                            <span>{{ item.title }}</span>
                        </Link>
                    </div>

                    <div class="mt-6 pt-5 border-t border-gray-200">
                        <div class="text-sm text-gray-500 mb-2">
                            Отдел продаж
                        </div>
                        <SalesDepartmentPhone class="text-base font-semibold"/>
                    </div>
                </div>
            </div>
        </div>

        <transition
            enter-active-class="transition-opacity duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-300"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="menuStore.isProfileSidenavOpen"
                @click.prevent="menuStore.toggleProfileSidenav"
                class="fixed inset-0 bg-black/50 z-40"
            ></div>
        </transition>
    </div>
</template>

<style scoped lang="scss">
</style>
